import { useCallback, useEffect, useRef, useState } from "react";

import {
  sectionAccessService,
  type PageWithAccess,
  type SectionWithAccess,
} from "./sectionAccessService";

import { invalidateNav } from "@/features/manual/api";
import { slugify } from "@/lib/slugify";
import { accessService } from "@/services/accessService";
import type { RoleSummary } from "@/types/access";
import { ApiError } from "@/types/api";

export interface ManagedNav {
  /** Complete section list with role access; null until loaded (or disabled). */
  sections: SectionWithAccess[] | null;
  roles: RoleSummary[];
  pagesBySection: Record<string, PageWithAccess[]>;
  /** Last failed operation, human-readable. Cleared on the next success. */
  opError: string | null;
  /** `${sectionId|pageId}:${roleId}` while a checkbox toggle is in flight. */
  busyKey: string | null;
  /** True while a reorder request is in flight — drags are disabled. */
  reordering: boolean;
  /** True while a create/rename/delete request is in flight. */
  mutating: boolean;
  ensurePages: (sectionId: string) => void;
  refreshPages: (sectionId: string) => void;
  toggleSectionRole: (sectionId: string, roleId: string) => Promise<void>;
  togglePageRole: (sectionId: string, pageId: string, roleId: string) => Promise<void>;
  /** Lift every restriction in one write (the popover's "everyone" state). */
  clearSectionRoles: (sectionId: string) => Promise<void>;
  clearPageRoles: (sectionId: string, pageId: string) => Promise<void>;
  reorderSections: (orderIds: string[]) => Promise<void>;
  reorderPages: (sectionId: string, orderIds: string[]) => Promise<void>;
  /** Each resolves true on success so the calling UI knows to close itself. */
  createSection: (title: string) => Promise<boolean>;
  renameSection: (sectionId: string, title: string) => Promise<boolean>;
  deleteSection: (sectionId: string) => Promise<boolean>;
  deletePage: (sectionId: string, pageId: string) => Promise<boolean>;
}

const CSRF_ERROR = "Your session could not be verified. Refresh the page and try again.";

/** Prefer the server's own message for expected conflicts (409: slug taken,
 * section not empty); anything else gets the caller's generic fallback. */
function messageFrom(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.status === 409 ? err.message : fallback;
}

/**
 * Admin-side data + mutations behind the sidebar's manage affordances.
 * Fetches the complete section list (and lazily each section's pages) for
 * cms.manage holders, and applies access/reorder changes optimistically,
 * reloading from the server on failure. Every successful mutation
 * invalidates the shared nav cache so the tree everyone sees stays true.
 */
export function useManagedNav(enabled: boolean, csrfToken: string | null): ManagedNav {
  const [sections, setSections] = useState<SectionWithAccess[] | null>(null);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [pagesBySection, setPagesBySection] = useState<Record<string, PageWithAccess[]>>({});
  const [opError, setOpError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [mutating, setMutating] = useState(false);
  const pagesRequested = useRef(new Set<string>());

  const loadSections = useCallback(async () => {
    const { sections: fresh } = await sectionAccessService.listSections();
    setSections(fresh);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void Promise.all([sectionAccessService.listSections(), accessService.getCatalog()])
      .then(([{ sections: sec }, catalog]) => {
        if (cancelled) return;
        setSections(sec);
        // Staff is universal (ticking it here would mean "everyone", already
        // the no-ticks default) and a superadmin-only restriction on client
        // content makes no sense — exclude both from the popover's role list.
        setRoles(catalog.roles.filter((r) => r.key !== "staff" && r.key !== "superadmin"));
      })
      .catch(() => {
        if (!cancelled) setOpError("Could not load section management data.");
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const loadPages = useCallback(async (sectionId: string) => {
    try {
      const { pages } = await sectionAccessService.listPages(sectionId);
      setPagesBySection((prev) => ({ ...prev, [sectionId]: pages }));
    } catch {
      pagesRequested.current.delete(sectionId);
      setOpError("Could not load this section's pages.");
    }
  }, []);

  const ensurePages = useCallback(
    (sectionId: string) => {
      if (!enabled || pagesRequested.current.has(sectionId)) return;
      pagesRequested.current.add(sectionId);
      void loadPages(sectionId);
    },
    [enabled, loadPages]
  );

  // Force a refetch of a section's page list — used when the nav tree reveals
  // a page the cached admin list doesn't have yet (e.g. one just created), so
  // the new page gains its access data and manage controls without a reload.
  const refreshPages = useCallback(
    (sectionId: string) => {
      if (!enabled) return;
      void loadPages(sectionId);
    },
    [enabled, loadPages]
  );

  const toggleSectionRole = useCallback(
    async (sectionId: string, roleId: string) => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return;
      }
      const current = sections?.find((s) => s.id === sectionId);
      if (!current) return;
      const nextRoleIds = current.roleIds.includes(roleId)
        ? current.roleIds.filter((id) => id !== roleId)
        : [...current.roleIds, roleId];
      setBusyKey(`${sectionId}:${roleId}`);
      try {
        await sectionAccessService.setAccess(sectionId, nextRoleIds, csrfToken);
        setSections(
          (prev) =>
            prev?.map((s) => (s.id === sectionId ? { ...s, roleIds: nextRoleIds } : s)) ?? prev
        );
        setOpError(null);
        invalidateNav();
      } catch {
        setOpError("Could not update section access. Try again.");
      } finally {
        setBusyKey(null);
      }
    },
    [csrfToken, sections]
  );

  const togglePageRole = useCallback(
    async (sectionId: string, pageId: string, roleId: string) => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return;
      }
      const current = pagesBySection[sectionId]?.find((p) => p.id === pageId);
      if (!current) return;
      const nextRoleIds = current.roleIds.includes(roleId)
        ? current.roleIds.filter((id) => id !== roleId)
        : [...current.roleIds, roleId];
      setBusyKey(`${pageId}:${roleId}`);
      try {
        await sectionAccessService.setPageAccess(pageId, nextRoleIds, csrfToken);
        setPagesBySection((prev) => ({
          ...prev,
          [sectionId]: (prev[sectionId] ?? []).map((p) =>
            p.id === pageId ? { ...p, roleIds: nextRoleIds } : p
          ),
        }));
        setOpError(null);
        invalidateNav();
      } catch {
        setOpError("Could not update page access. Try again.");
      } finally {
        setBusyKey(null);
      }
    },
    [csrfToken, pagesBySection]
  );

  const clearSectionRoles = useCallback(
    async (sectionId: string) => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return;
      }
      setBusyKey(`${sectionId}:clear`);
      try {
        await sectionAccessService.setAccess(sectionId, [], csrfToken);
        setSections(
          (prev) => prev?.map((s) => (s.id === sectionId ? { ...s, roleIds: [] } : s)) ?? prev
        );
        setOpError(null);
        invalidateNav();
      } catch {
        setOpError("Could not update section access. Try again.");
      } finally {
        setBusyKey(null);
      }
    },
    [csrfToken]
  );

  const clearPageRoles = useCallback(
    async (sectionId: string, pageId: string) => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return;
      }
      setBusyKey(`${pageId}:clear`);
      try {
        await sectionAccessService.setPageAccess(pageId, [], csrfToken);
        setPagesBySection((prev) => ({
          ...prev,
          [sectionId]: (prev[sectionId] ?? []).map((p) =>
            p.id === pageId ? { ...p, roleIds: [] } : p
          ),
        }));
        setOpError(null);
        invalidateNav();
      } catch {
        setOpError("Could not update page access. Try again.");
      } finally {
        setBusyKey(null);
      }
    },
    [csrfToken]
  );

  const reorderSections = useCallback(
    async (orderIds: string[]) => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return;
      }
      setReordering(true);
      // Optimistic: reflect the new order (and 1..N ordinals) immediately.
      setSections((prev) => {
        if (!prev) return prev;
        const byId = new Map(prev.map((s) => [s.id, s]));
        return orderIds
          .map((id, i) => {
            const s = byId.get(id);
            return s ? { ...s, ordinal: i + 1 } : null;
          })
          .filter((s): s is SectionWithAccess => s !== null);
      });
      try {
        const { sections: fresh } = await sectionAccessService.reorder(orderIds, csrfToken);
        setSections(fresh);
        setOpError(null);
        invalidateNav();
      } catch {
        await loadSections().catch(() => undefined);
        setOpError("Could not reorder sections. Try again.");
      } finally {
        setReordering(false);
      }
    },
    [csrfToken, loadSections]
  );

  const reorderPages = useCallback(
    async (sectionId: string, orderIds: string[]) => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return;
      }
      setReordering(true);
      setPagesBySection((prev) => {
        const current = prev[sectionId];
        if (!current) return prev;
        const byId = new Map(current.map((p) => [p.id, p]));
        const next = orderIds
          .map((id, i) => {
            const p = byId.get(id);
            return p ? { ...p, ordinal: i + 1 } : null;
          })
          .filter((p): p is PageWithAccess => p !== null);
        return { ...prev, [sectionId]: next };
      });
      try {
        const { pages: fresh } = await sectionAccessService.reorderPages(
          sectionId,
          orderIds,
          csrfToken
        );
        setPagesBySection((prev) => ({ ...prev, [sectionId]: fresh }));
        setOpError(null);
        invalidateNav();
      } catch {
        await loadPages(sectionId);
        setOpError("Could not reorder pages. Try again.");
      } finally {
        setReordering(false);
      }
    },
    [csrfToken, loadPages]
  );

  const createSection = useCallback(
    async (title: string): Promise<boolean> => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return false;
      }
      const slug = slugify(title);
      if (!slug) {
        setOpError("The section title needs at least one letter or number.");
        return false;
      }
      setMutating(true);
      try {
        const { sections: fresh } = await sectionAccessService.createSection(
          slug,
          title.trim(),
          csrfToken
        );
        setSections(fresh);
        setOpError(null);
        invalidateNav();
        return true;
      } catch (err) {
        setOpError(messageFrom(err, "Could not create the section. Try again."));
        return false;
      } finally {
        setMutating(false);
      }
    },
    [csrfToken]
  );

  const renameSection = useCallback(
    async (sectionId: string, title: string): Promise<boolean> => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return false;
      }
      const trimmed = title.trim();
      if (!trimmed) {
        setOpError("Give the section a title first.");
        return false;
      }
      setMutating(true);
      try {
        await sectionAccessService.renameSection(sectionId, trimmed, csrfToken);
        setSections(
          (prev) => prev?.map((s) => (s.id === sectionId ? { ...s, title: trimmed } : s)) ?? prev
        );
        setOpError(null);
        invalidateNav();
        return true;
      } catch {
        setOpError("Could not rename the section. Try again.");
        return false;
      } finally {
        setMutating(false);
      }
    },
    [csrfToken]
  );

  const deleteSection = useCallback(
    async (sectionId: string): Promise<boolean> => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return false;
      }
      setMutating(true);
      try {
        const { sections: fresh } = await sectionAccessService.deleteSection(sectionId, csrfToken);
        setSections(fresh);
        setOpError(null);
        invalidateNav();
        return true;
      } catch (err) {
        setOpError(messageFrom(err, "Could not delete the section. Try again."));
        return false;
      } finally {
        setMutating(false);
      }
    },
    [csrfToken]
  );

  const deletePage = useCallback(
    async (sectionId: string, pageId: string): Promise<boolean> => {
      if (!csrfToken) {
        setOpError(CSRF_ERROR);
        return false;
      }
      setMutating(true);
      try {
        await sectionAccessService.deletePage(pageId, csrfToken);
        await loadPages(sectionId);
        setOpError(null);
        invalidateNav();
        return true;
      } catch (err) {
        setOpError(messageFrom(err, "Could not delete the page. Try again."));
        return false;
      } finally {
        setMutating(false);
      }
    },
    [csrfToken, loadPages]
  );

  return {
    sections: enabled ? sections : null,
    roles,
    pagesBySection,
    opError,
    busyKey,
    reordering,
    mutating,
    ensurePages,
    refreshPages,
    toggleSectionRole,
    togglePageRole,
    clearSectionRoles,
    clearPageRoles,
    reorderSections,
    reorderPages,
    createSection,
    renameSection,
    deleteSection,
    deletePage,
  };
}
